/**
 * VISTOERA_TENANT_ADMIN_360_..._P0_V1 (task spec §26/§72) — Tenant 360
 * Overview's "积分消耗分布" multi-slice donut + legend.
 *
 * Distinct from `DonutGauge` (a single-value percent gauge used
 * elsewhere for Storage) — this one draws N arcs (one per Function
 * Node / Operation, amount-based per task spec §26) plus a legend with
 * per-slice amount/percent, so it is a new component rather than a
 * misuse of that one. No charting library exists in this repo (same
 * audit as `CreditTrendChart`).
 */
export interface CreditBreakdownSliceInput {
  label: string
  amount: number
  percent: number
}

const SLICE_COLORS = [
  'var(--pf-primary, #5B5FF5)',
  'var(--pf-success, #22C55E)',
  'var(--pf-warning, #F59E0B)',
  'var(--pf-info, #06B6D4)',
  'var(--pf-danger, #EF4444)',
  '#A855F7',
  '#64748B',
]

export default function CreditBreakdownDonut({
  slices,
  totalLabel,
  emptyLabel,
  formatAmount,
  size = 176,
  strokeWidth = 26,
}: {
  slices: CreditBreakdownSliceInput[]
  totalLabel: string
  emptyLabel: string
  formatAmount: (v: number) => string
  size?: number
  strokeWidth?: number
}) {
  const total = slices.reduce((sum, s) => sum + s.amount, 0)

  if (slices.length === 0 || total <= 0) {
    return (
      <div style={{ height: size, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
        {emptyLabel}
      </div>
    )
  }

  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius

  let cursor = 0
  const arcs = slices.map((slice, i) => {
    const fraction = total > 0 ? slice.amount / total : 0
    const dash = fraction * circumference
    const gap = circumference - dash
    const offset = -cursor * circumference
    cursor += fraction
    return { ...slice, dash, gap, offset, color: SLICE_COLORS[i % SLICE_COLORS.length] }
  })

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }} role="img" aria-label={totalLabel}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {arcs.map((arc, i) => (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={arc.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${arc.dash} ${arc.gap}`}
              strokeDashoffset={arc.offset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            >
              <title>
                {arc.label}: {formatAmount(arc.amount)} ({arc.percent.toFixed(1)}%)
              </title>
            </circle>
          ))}
        </svg>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.2 }}>{formatAmount(total)}</span>
          <span style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{totalLabel}</span>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
        {arcs.map((arc, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: arc.color, flexShrink: 0 }} />
            <span style={{ color: 'var(--text-primary)', flex: 1 }}>{arc.label}</span>
            <span style={{ color: 'var(--text-secondary)' }}>{arc.percent.toFixed(0)}%</span>
            <span style={{ color: 'var(--text-secondary)', minWidth: 56, textAlign: 'right' }}>{formatAmount(arc.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
